<template>
    <section
        v-if="headings.length > 0"
        class="table-of-content"
    >
        <p class="table-of-content__title u-header-3">
            In this article:
        </p>
        <ul :class="`table-of-content__list table-of-content__list--base-${lowestHeading}`">
            <li
                v-for="heading in headings"
                :key="heading.id"
                class="table-of-content__item"
                :class="{
                    [`table-of-content__item--${heading.level}`]: true,
                    'table-of-content__item--active': activeItem === heading.id,
                }"
            >
                <a
                    :href="`#${heading.id}`"
                    @click="scrollToHeading($event, heading)"
                >
                    {{ heading.title }}
                </a>
            </li>
        </ul>
    </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { getTocHeadings } from '#shared/articleHeadings'

// The heading list is resolved on the server: `/api/article` attaches it to the payload
// as `contentHeadings`. When the story arrives over that API the array is used as-is
// (stable de-dup ids, computed once and cached); if it's ever handed in raw — the
// Storyblok Visual Editor renders the story client-side without the API — it is derived
// here by the same walk for parity.
const props = defineProps({
    blok: {
        type: Object,
        default: () => ({}),
    },
    story: {
        type: Object,
        default: null,
    },
})

const { capture } = useTracking()

const headings = computed(() => {
    if (props.story?.contentHeadings) {
        return props.story.contentHeadings
    }
    return props.story?.content ? getTocHeadings(props.story.content) : []
})
const activeItem = ref('')

// Scroll from JavaScript instead of the native fragment jump. The native jump has two
// faults. It reads the scroll offset at click time, while the page still settles, so the
// reader stops above the heading. It also does nothing on a repeat click, because the URL
// hash already matches. scrollIntoView reads the layout on each click, so a repeat click
// scrolls again, and it honours the scroll-padding-top set on `html` in main.scss, which is
// what keeps the heading clear of the sticky header.
function scrollToHeading(event, heading) {
    capture('table_of_content_click', { heading: heading.title })
    const element = document.getElementById(heading.id)
    if (!element)
        return
    event.preventDefault()
    element.scrollIntoView()
    history.pushState(null, '', `#${heading.id}`)
}

// Feeds `--base-N`, whose negative margin (N * -24px) counteracts the per-item
// `padding-left: level * 24px` so the top-level entries align with the list gutter instead
// of being pushed right (or worse, yanked left past the edge by an oversized base). For a
// uniform h2 list that is --base-2 (-48px -> h2 at 0); h3-fallback lists get --base-3. So
// this must be the lowest heading level present — not a maximum.
const lowestHeading = computed(() =>
    headings.value.length ? Math.min(...headings.value.map(h => h.level)) : 3,
)

function scrollEvent() {
    const activeHeading = headings.value.reduce((acc, heading) => {
        const element = document.getElementById(heading.id)
        if (!element)
            return acc
        if (element.getBoundingClientRect().top < 100)
            return heading
        return acc
    }, null)

    if (activeHeading)
        activeItem.value = activeHeading.id
}

onMounted(() => {
    if (!headings.value.length)
        return
    activeItem.value = headings.value[0].id
    window.addEventListener('scroll', scrollEvent)
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', scrollEvent)
})
</script>

<style lang="scss" scoped>
@use 'table-of-content';
</style>
